import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  BarChart3, Users, MessageSquare, Package,
  LogOut, RefreshCw, CheckCircle, Clock, ChevronRight, ChevronLeft, X, Lock, EyeOff, Eye, Search, ArrowLeft, User, Activity, Database, FileText, AlertTriangle, Trash2, RotateCcw, Utensils
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import UnknownProductsQueue from "@/components/UnknownProductsQueue";

function AdminLock({ api, onUnlock }) {
  const { t } = useLanguage();
  const [secret, setSecret] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api().post("/admin/verify", { secret });
      sessionStorage.setItem("admin_verified", "1");
      onUnlock();
    } catch {
      setError(t("adminWrongPassword"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F3] flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-sm border border-black/5 p-8 w-full max-w-sm">
        <div className="flex justify-center mb-6">
          <div className="w-12 h-12 bg-[#7B9E7F]/10 rounded-2xl flex items-center justify-center">
            <Lock className="w-6 h-6 text-[#7B9E7F]" />
          </div>
        </div>
        <h2 className="text-lg font-semibold text-[#3D4035] text-center mb-1">{t("adminAccessTitle")}</h2>
        <p className="text-sm text-gray-400 text-center mb-6">{t("adminAccessSub")}</p>
        <form onSubmit={submit} className="space-y-4">
          <div className="relative">
            <input
              type={show ? "text" : "password"}
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={t("adminPassword")}
              autoFocus
              className="w-full border border-gray-200 rounded-xl px-4 py-3 pr-10 text-sm focus:outline-none focus:border-[#7B9E7F] text-[#3D4035]"
            />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {error && <p className="text-red-500 text-sm text-center">{error}</p>}
          <button
            type="submit"
            disabled={loading || !secret}
            className="w-full py-3 bg-[#7B9E7F] text-white rounded-xl text-sm font-medium hover:bg-[#6a8d6e] transition-colors disabled:opacity-50"
          >
            {loading ? t("adminVerifying") : t("adminEnter")}
          </button>
        </form>
      </div>
    </div>
  );
}

const SECTION_IDS = [
  { id: "dashboard", tKey: "adminDashboard", icon: BarChart3 },
  { id: "users", tKey: "adminUsers", icon: Users },
  { id: "products", tKey: "adminUnknownProducts", icon: Package },
  { id: "support", tKey: "adminSupport", icon: MessageSquare },
];

const CATEGORIES = [
  "meat", "deli_meat", "seafood", "dairy_eggs", "eggs", "vegetables",
  "mushrooms", "fruits_berries", "grains_bakery", "nuts_seeds",
  "spices_oils", "sweets_snacks", "frozen_foods", "beverages", "other",
];

function StatCard({ label, value, sub, color = "#7B9E7F" }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-black/5">
      <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-3xl font-semibold" style={{ color }}>{value ?? "—"}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

// One journey, in order. prevKey names the step immediately before, so the
// step-to-step share always compares neighbours: the cards used to measure
// signups against generations and payments against signups — skipping a step
// each time while the caption still read "від попереднього".
//
// The last two steps are the only ones measuring a subscription rather than
// an event, and they measure it differently: "Брав тріал" is cumulative,
// "Платить зараз" is a snapshot, so the share between them compares two
// different kinds of number. Left as is on purpose - what each one counts is
// spelled out in the block under the grid, not in a caption inside the card:
// at one seventh of the width, a line that long would stretch all seven.
//
// "Зайшов на сайт" counts anyone with a first touch, landing or demo. It only
// means something from the day landing_view shipped; before that it equals
// "Відкрив демо" because nothing else was ever recorded.
// null = every visitor ever. Must match FUNNEL_PERIODS on the server, which
// falls back to all time for anything it does not offer.
const FUNNEL_WINDOWS = [
  { days: 7, label: "7 днів" },
  { days: 30, label: "30 днів" },
  { days: 90, label: "90 днів" },
  { days: null, label: "весь час" },
];

const FUNNEL_STEPS = [
  // `short` is the column header in the by-channel table. It lives here rather
  // than in a table of its own so a card and its column cannot drift apart -
  // and where the short form loses something ("Тріал" drops "ever", "Платить"
  // drops "right now"), the full label is what the header carries on hover.
  { key: "entered",   label: "Зайшов на сайт",  prevKey: null,        short: "Зайшов" },
  { key: "viewed",    label: "Відкрив демо",    prevKey: "entered",   short: "Демо" },
  { key: "generated", label: "Згенерував день", prevKey: "viewed",    short: "Генерація" },
  { key: "cta",       label: "Натиснув CTA",    prevKey: "generated", short: "CTA" },
  { key: "signed_up", label: "Зареєструвався",  prevKey: "cta",       short: "Реєстрація" },
  { key: "trials",    label: "Брав тріал",      prevKey: "signed_up", short: "Тріал" },
  { key: "paid",      label: "Платить зараз",   prevKey: "trials",    short: "Платить" },
];

function Dashboard({ api }) {
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  // Funnel window only. The cards above it (users, MAU, retention) have their
  // own fixed periods baked in, so this must not read as a page-wide filter —
  // it sits inside the funnel block for that reason.
  const [funnelDays, setFunnelDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = funnelDays ? `?funnel_days=${funnelDays}` : "";
      const res = await api().get(`/admin/stats${q}`);
      setStats(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [api, funnelDays]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [load]);

  if (loading) return <div className="flex justify-center pt-20"><div className="w-8 h-8 border-2 border-[#7B9E7F]/20 border-t-[#7B9E7F] rounded-full animate-spin" /></div>;
  if (!stats) return <p className="text-red-500">{t("adminLoadError")}</p>;

  const healthColor = (val, goodThreshold, warnThreshold) => {
    if (val === null || val === undefined) return "#9CA3AF";
    if (val >= goodThreshold) return "#10B981";
    if (val >= warnThreshold) return "#F59E0B";
    return "#EF4444";
  };

  const fmtPct = (val) => (val === null || val === undefined) ? "—" : `${val}%`;
  // Share of the previous funnel step. No base, no number: "100%" out of zero
  // visitors would read as success rather than as absence of data.
  const fmtShare = (part, whole) =>
    whole ? `${Math.round((part / whole) * 100)}% від попереднього` : "—";
  // Second base: the same number against the top of the funnel. Step-to-step
  // shares alone flatter a funnel — five steps at 70% each read as healthy and
  // still deliver 17 people out of 100.
  const fmtOfAll = (part, whole) =>
    whole ? `${Math.round((part / whole) * 100)}% від усіх` : "—";
  const fmtWoW = (val) => (val === null || val === undefined) ? "—" : (val >= 0 ? `+${val}%` : `${val}%`);

  // Absent, not empty, while a browser still holds a response from before the
  // breakdown shipped. On screen the two mean the same thing.
  const bySource = stats.demo_funnel?.by_source || [];

  const signupChartData = (stats.signups_by_day || []).map((d) => ({
    label: d.date ? d.date.slice(8, 10) : "",
    fullDate: d.date,
    count: d.count,
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-[#3D4035]">Аналітика</h2>
        <button onClick={load} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
          <RefreshCw className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Усього юзерів" value={stats.total_users} sub="з запуску 13 травня" />
        <StatCard
          label="MAU · 30 днів"
          value={stats.mau}
          sub={stats.total_users ? `${Math.round(stats.mau / stats.total_users * 100)}% від total` : ""}
        />
        <StatCard
          label="D7 retention"
          value={fmtPct(stats.d7_retention_pct)}
          sub="target 30%+"
          color={healthColor(stats.d7_retention_pct, 30, 10)}
        />
        <StatCard
          label="D30 retention"
          value={fmtPct(stats.d30_retention_pct)}
          sub="target 20%+"
          color={healthColor(stats.d30_retention_pct, 20, 5)}
        />
      </div>

      {stats.demo_funnel && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Воронка · від приходу до оплати
            </h3>
            <div className="flex items-center gap-1">
              {FUNNEL_WINDOWS.map(({ days, label }) => (
                <button
                  key={label}
                  onClick={() => setFunnelDays(days)}
                  className={`px-2.5 py-1 rounded-full text-xs transition-colors ${
                    funnelDays === days
                      ? "bg-[#7B9E7F] text-white"
                      : "text-gray-400 hover:bg-gray-100"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
            <span>
              Люди зараховані до періоду за <strong>першим приходом</strong>; далі рахуються всі
              їхні кроки, навіть якщо реєстрація сталась пізніше.
            </span>
            {funnelDays === 7 && <span>Останній тиждень ще дозріває — оплати відстають.</span>}
            {stats.demo_funnel.direct_to_demo > 0 && (
              <span>
                {stats.demo_funnel.direct_to_demo} зайшли одразу в демо, повз лендінг
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {FUNNEL_STEPS.map(({ label, key, prevKey }) => {
              const value = stats.demo_funnel[key];
              // Every step is measured twice: against the step before it, and
              // against everyone who arrived. The first says where people are
              // lost, the second says how much of the whole is left.
              const prev = prevKey ? stats.demo_funnel[prevKey] : null;
              return (
                <StatCard
                  key={key}
                  label={label}
                  value={value}
                  sub={
                    prevKey && (
                      <>
                        <span className="block">{fmtShare(value, prev)}</span>
                        <span className="block">{fmtOfAll(value, stats.demo_funnel.entered)}</span>
                      </>
                    )
                  }
                />
              );
            })}
          </div>
          {/* What the last two cards actually count, plus the two numbers the
              funnel must be read against. Under the grid rather than inside
              the cards: at one seventh of the width a caption this long would
              stretch all seven. past_due and date_unreadable_counted have been
              in the API response since 25.08 and reach the screen here for the
              first time - the funnel was being read without them. */}
          <div className="space-y-1 text-xs text-gray-400">
            <p>
              <span className="text-gray-500">«Брав тріал»</span> — коли-небудь починав
              пробний період; хто вже платить теж тут
            </p>
            <p>
              <span className="text-gray-500">«Платить зараз»</span> — активна підписка
              зі Stripe; подарований доступ не рахується; хто відвалився — зник
            </p>
            <p>
              <span className="text-gray-500">
                Зірвався платіж: <strong>{stats.demo_funnel.past_due ?? 0}</strong>
              </span>{" "}
              — підписка жива, платіж не пройшов, у продукт не пускає
            </p>
            {(stats.demo_funnel.date_unreadable_counted ?? 0) > 0 ? (
              <p>
                <span className="text-gray-500">
                  Нечитаних дат: <strong>{stats.demo_funnel.date_unreadable_counted}</strong>
                </span>{" "}
                — у цих акаунтів дата кінця періоду не читається,
                тож їх пораховано як живі. Перевір руками.
              </p>
            ) : (
              <p>Нечитаних дат немає</p>
            )}
          </div>
        </div>
      )}

      {stats.demo_funnel && (
        <div className="space-y-3">
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">
            Канали · той самий період
          </h3>
          <p className="text-xs text-gray-400">
            Канал визначається за <strong>першим дотиком</strong> і закріплюється за
            людиною назавжди: хто прийшов з реклами, а через тиждень повернувся
            напряму, лишається в рекламі.
          </p>
          {bySource.length === 0 ? (
            <p className="text-xs text-gray-400">Даних по каналах ще немає.</p>
          ) : (
            /* Eight columns want about 720px and the main area gives 896 at
               1024px - so it fits, with the sidebar overlaying rather than
               shrinking it. Below that the wrapper scrolls: a column that
               crushes is worse than a table that moves. */
            <div className="bg-white rounded-2xl shadow-sm border border-black/5 overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-gray-400 text-xs uppercase">
                    <th className="px-4 py-3 text-left font-medium">Канал</th>
                    {FUNNEL_STEPS.map(({ key, label, short }) => (
                      <th
                        key={key}
                        title={label}
                        className="px-3 py-3 text-right font-medium whitespace-nowrap"
                      >
                        {short}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bySource.map((row) => (
                    <tr key={row.source} className="border-b border-gray-50 last:border-0">
                      {/* A source is capped at 32 characters server-side, not at
                          ten: one long UTM would push the numbers off screen. */}
                      <td className="px-4 py-3 max-w-[200px] truncate" title={row.source}>
                        {row.source === "unattributed" ? (
                          <span className="text-gray-400">джерело невідоме</span>
                        ) : (
                          <span className="font-medium text-[#3D4035]">{row.source}</span>
                        )}
                      </td>
                      {FUNNEL_STEPS.map(({ key }) => (
                        /* Zeroes greyed out. Seven columns of digits read as one
                           block otherwise, and where a channel stops is the
                           whole point of splitting the funnel up. */
                        <td
                          key={key}
                          className={`px-3 py-3 text-right tabular-nums ${
                            row[key] ? "text-[#3D4035]" : "text-gray-300"
                          }`}
                        >
                          {row[key]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-gray-400">
            <span className="text-gray-500">«джерело невідоме»</span> — не «прийшли
            напряму»: джерела в базі немає взагалі. Майже всі такі люди приходили
            до того, як відстеження з'явилось.
          </p>
        </div>
      )}

      <div className="space-y-3">
        <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Зростання</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard label="Цей тиждень" value={stats.signups_this_week} />
          <StatCard label="Минулий тиждень" value={stats.signups_last_week} />
          <StatCard
            label="WoW growth"
            value={fmtWoW(stats.wow_growth_pct)}
            color={stats.wow_growth_pct > 0 ? "#10B981" : stats.wow_growth_pct < 0 ? "#EF4444" : "#9CA3AF"}
          />
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-black/5">
          <div className="flex justify-between items-baseline mb-3">
            <p className="text-sm text-gray-500">Реєстрації по днях</p>
            <p className="text-xs text-gray-400">останні 30 днів</p>
          </div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={signupChartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={20} />
                <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip
                  cursor={{ fill: "rgba(123, 158, 127, 0.1)" }}
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }}
                  labelFormatter={(label, items) => items?.[0]?.payload?.fullDate || label}
                  formatter={(value) => [`${value} signup${value === 1 ? "" : "s"}`, ""]}
                />
                <Bar dataKey="count" fill="#7B9E7F" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Поточна активність</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Online now" value={stats.online_now} sub="останні 15 хв" color="#10B981" />
          <StatCard label="DAU" value={stats.dau} sub="за 24 години" />
          <StatCard label="WAU" value={stats.wau} sub="за 7 днів" />
          <StatCard label="Нові сьогодні" value={stats.new_today} />
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Контент і операції</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Meal plans"
            value={stats.total_plans}
            sub={stats.total_users ? `${(stats.total_plans / stats.total_users).toFixed(1)} на юзера` : ""}
          />
          <StatCard label="Recipes" value={stats.total_recipes} sub="в каталозі" />
          <StatCard
            label="Grocery lists"
            value={stats.total_grocery}
            sub={stats.total_users ? `${(stats.total_grocery / stats.total_users).toFixed(2)} на юзера` : ""}
          />
          <StatCard
            label="Невідомі продукти"
            value={stats.unknown_products}
            sub={stats.unknown_products > 0 ? "потребують review" : "усі розпізнані"}
            color={stats.unknown_products > 0 ? "#F59E0B" : "#7B9E7F"}
          />
        </div>
      </div>

      <div className="bg-white rounded-2xl p-4 shadow-sm border border-black/5 text-sm text-gray-500">
        Версія API: <span className="font-medium text-[#3D4035]">MealPill 1.0</span> · Оновлено: <span className="font-medium text-[#3D4035]">{new Date().toLocaleString("uk-UA")}</span>
      </div>
    </div>
  );
}

function UsersSection({ api }) {
  const [selectedUserId, setSelectedUserId] = useState(null);

  if (selectedUserId) {
    return (
      <UserDetailView
        api={api}
        userId={selectedUserId}
        onBack={() => setSelectedUserId(null)}
      />
    );
  }

  return <UserList api={api} onSelect={setSelectedUserId} />;
}

function UserList({ api, onSelect }) {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const LIMIT = 20;

  const load = useCallback(async (off = 0, currentSearch = search, currentFilter = filter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(LIMIT),
        offset: String(off),
      });
      if (currentSearch) params.append("search", currentSearch);
      if (currentFilter && currentFilter !== "all") params.append("filter", currentFilter);

      const res = await api().get(`/admin/users?${params.toString()}`);
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [api, search, filter]);

  useEffect(() => {
    const t = setTimeout(() => {
      setOffset(0);
      load(0, search, filter);
    }, 300);
    return () => clearTimeout(t);
  }, [search, filter, load]);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("uk-UA", { day: "2-digit", month: "short", year: "2-digit" }) : "—";
  const fmtTime = (d) => d ? new Date(d).toLocaleString("uk-UA", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

  const filters = [
    { id: "all", label: "Усі" },
    { id: "active_7d", label: "Активні 7 днів" },
    { id: "new_week", label: "Нові цього тижня" },
    { id: "inactive_30d", label: "Неактивні 30+ днів" },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-[#3D4035]">
          Користувачі <span className="text-sm font-normal text-gray-400">({total})</span>
        </h2>
        <button onClick={() => load(offset, search, filter)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
          <RefreshCw className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 px-4 py-2.5 flex items-center gap-2">
        <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Пошук по email або імені..."
          className="flex-1 text-sm focus:outline-none text-[#3D4035] placeholder:text-gray-400 bg-transparent"
        />
        {search && (
          <button onClick={() => setSearch("")} className="text-gray-400 hover:text-[#3D4035]">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        {filters.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filter === f.id
                ? "bg-[#7B9E7F] text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center pt-10"><div className="w-8 h-8 border-2 border-[#7B9E7F]/20 border-t-[#7B9E7F] rounded-full animate-spin" /></div>
      ) : users.length === 0 ? (
        <div className="text-center text-gray-400 pt-10">
          <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p>Користувачів не знайдено</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-black/5 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-gray-400 text-xs uppercase">
                <th className="px-4 py-3 text-left">Ім'я / Email</th>
                <th className="px-4 py-3 text-left">Мова</th>
                <th className="px-4 py-3 text-left">Плани</th>
                <th className="px-4 py-3 text-left">Реєстрація</th>
                <th className="px-4 py-3 text-left">Активність</th>
                <th className="px-4 py-3 text-left">Статус</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr
                  key={u.id}
                  onClick={() => onSelect(u.id)}
                  className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <p className="font-medium text-[#3D4035]">{u.name || "—"}</p>
                    <p className="text-gray-400 text-xs">{u.email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 bg-gray-100 rounded-full text-xs font-medium">{u.language || "uk"}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{u.plan_count ?? 0}</td>
                  <td className="px-4 py-3 text-gray-500">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3 text-gray-500">{fmtTime(u.last_seen)}</td>
                  <td className="px-4 py-3">
                    {u.deleted_at ? (
                      <span className="text-red-500 text-xs font-medium">Видалено</span>
                    ) : u.onboarding_completed ? (
                      <span className="text-green-500 flex items-center gap-1 text-xs"><CheckCircle className="w-3.5 h-3.5" /> Onboarded</span>
                    ) : (
                      <span className="text-gray-400 flex items-center gap-1 text-xs"><Clock className="w-3.5 h-3.5" /> Onboarding</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <ChevronRight className="w-4 h-4 text-gray-300" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {total > LIMIT && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <button
                disabled={offset === 0}
                onClick={() => { const o = Math.max(0, offset - LIMIT); setOffset(o); load(o, search, filter); }}
                className="text-sm text-[#7B9E7F] disabled:text-gray-300"
              >← Назад</button>
              <span className="text-xs text-gray-400">{offset + 1}–{Math.min(offset + LIMIT, total)} з {total}</span>
              <button
                disabled={offset + LIMIT >= total}
                onClick={() => { const o = offset + LIMIT; setOffset(o); load(o, search, filter); }}
                className="text-sm text-[#7B9E7F] disabled:text-gray-300"
              >Далі →</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function UserDetailView({ api, userId, onBack }) {
  const [user, setUser] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [userRes, dataRes] = await Promise.all([
        api().get(`/admin/users/${userId}`),
        api().get(`/admin/users/${userId}/data`),
      ]);
      setUser(userRes.data);
      setData(dataRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [api, userId]);

  useEffect(() => { load(); }, [load]);

  const handleSoftDelete = async () => {
    if (!window.confirm(`Soft-delete юзера ${user.email}?\nДані лишаються 30 днів, потім автоматичний hard-delete.`)) return;
    setActionLoading(true);
    try {
      await api().delete(`/admin/users/${userId}`);
      await load();
    } catch (e) {
      console.error(e);
      alert("Помилка видалення");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestore = async () => {
    setActionLoading(true);
    try {
      await api().post(`/admin/users/${userId}/restore`);
      await load();
    } catch (e) {
      console.error(e);
      alert("Помилка відновлення");
    } finally {
      setActionLoading(false);
    }
  };

  const tabs = [
    { id: "overview", label: "Огляд", icon: User },
    { id: "activity", label: "Активність", icon: Activity, stub: true },
    { id: "data", label: "Дані", icon: Database },
    { id: "support", label: "Підтримка", icon: MessageSquare },
    { id: "notes", label: "Нотатки", icon: FileText, stub: true },
    { id: "food_profile", label: "Харчовий профіль", icon: Utensils },
    { id: "danger", label: "Danger", icon: AlertTriangle },
  ];

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("uk-UA", { day: "2-digit", month: "short", year: "numeric" }) : "—";
  const fmtDateTime = (d) => d ? new Date(d).toLocaleString("uk-UA") : "—";

  if (loading) return <div className="flex justify-center pt-20"><div className="w-8 h-8 border-2 border-[#7B9E7F]/20 border-t-[#7B9E7F] rounded-full animate-spin" /></div>;
  if (!user) return <p className="text-red-500">Користувача не знайдено</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-semibold text-[#3D4035] truncate">{user.email}</h2>
          {user.name && <p className="text-sm text-gray-500">{user.name}</p>}
        </div>
        {user.deleted_at && (
          <span className="px-3 py-1 bg-red-50 text-red-600 rounded-full text-xs font-medium flex-shrink-0">
            Soft-deleted
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4">
        <nav className="space-y-1">
          {tabs.map(({ id, label, icon: Icon, stub }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                tab === id
                  ? id === "danger"
                    ? "bg-red-50 text-red-600"
                    : "bg-[#7B9E7F]/10 text-[#7B9E7F]"
                  : id === "danger"
                    ? "text-red-400 hover:bg-red-50/50"
                    : "text-gray-500 hover:bg-gray-50"
              }`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1 text-left">{label}</span>
              {stub && <span className="text-[10px] text-gray-300 uppercase">soon</span>}
            </button>
          ))}
        </nav>

        <div className="bg-white rounded-2xl shadow-sm border border-black/5 p-6 min-h-[300px]">
          {tab === "overview" && (
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Огляд</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                <div>
                  <p className="text-gray-400 text-xs uppercase mb-0.5">Email</p>
                  <p className="text-[#3D4035] font-medium break-all">{user.email}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs uppercase mb-0.5">Ім'я</p>
                  <p className="text-[#3D4035] font-medium">{user.name || "—"}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs uppercase mb-0.5">Мова</p>
                  <p className="text-[#3D4035] font-medium">{user.language || "uk"}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs uppercase mb-0.5">Family size</p>
                  <p className="text-[#3D4035] font-medium">{user.family_size || "—"}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs uppercase mb-0.5">Зареєстрований</p>
                  <p className="text-[#3D4035] font-medium">{fmtDate(user.created_at)}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs uppercase mb-0.5">Остання активність</p>
                  <p className="text-[#3D4035] font-medium">{fmtDateTime(user.last_seen)}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs uppercase mb-0.5">Onboarding</p>
                  <p className="text-[#3D4035] font-medium">
                    {user.onboarding_completed ? "Завершено" : "Не завершено"}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs uppercase mb-0.5">ID</p>
                  <p className="text-[#3D4035] font-mono text-xs break-all">{user.id}</p>
                </div>
              </div>
            </div>
          )}

          {tab === "activity" && (
            <div className="text-center text-gray-400 py-12">
              <Activity className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Activity timeline — coming soon</p>
              <p className="text-xs mt-1">Logins, generations, key actions</p>
            </div>
          )}

          {tab === "data" && data && (
            <div className="space-y-6">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Дані юзера</h3>

              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-400 uppercase">Meal plans</p>
                  <p className="text-2xl font-semibold text-[#3D4035]">{data.plan_count}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-400 uppercase">Recipes</p>
                  <p className="text-2xl font-semibold text-[#3D4035]">{data.recipes_count}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-400 uppercase">Grocery lists</p>
                  <p className="text-2xl font-semibold text-[#3D4035]">{data.grocery_count}</p>
                </div>
              </div>

              {data.recent_plans?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Останні 10 планів</p>
                  <div className="space-y-1.5">
                    {data.recent_plans.map((p, i) => (
                      <div key={p.id || i} className="flex justify-between text-sm border-b border-gray-50 py-1.5">
                        <span className="text-[#3D4035] truncate flex-1">{p.title || `Plan ${(p.id || "").slice(0, 8)}`}</span>
                        <span className="text-gray-400 text-xs flex-shrink-0 ml-2">{fmtDate(p.created_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {data.recent_grocery_lists?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Останні 5 списків покупок</p>
                  <div className="space-y-1.5">
                    {data.recent_grocery_lists.map((g, i) => (
                      <div key={g.id || i} className="flex justify-between text-sm border-b border-gray-50 py-1.5">
                        <span className="text-[#3D4035] truncate flex-1">{g.title || `List ${(g.id || "").slice(0, 8)}`}</span>
                        <span className="text-gray-400 text-xs flex-shrink-0 ml-2">{fmtDate(g.created_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "support" && (
            <div className="text-center text-gray-400 py-12">
              <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Support tickets — coming soon</p>
              <p className="text-xs mt-1">Звернення цього юзера в підтримку</p>
            </div>
          )}

          {tab === "notes" && (
            <div className="text-center text-gray-400 py-12">
              <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Admin notes — coming soon</p>
              <p className="text-xs mt-1">Внутрішні нотатки команди</p>
            </div>
          )}

          {tab === "food_profile" && <FoodProfileTab api={api} userId={userId} />}

          {tab === "danger" && (
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-red-500 uppercase tracking-wider">Danger zone</h3>

              {user.deleted_at ? (
                <div className="bg-red-50 rounded-xl p-4 space-y-3">
                  <div>
                    <p className="text-sm font-medium text-red-600">Soft-deleted</p>
                    <p className="text-xs text-red-500 mt-0.5">
                      Видалено {fmtDateTime(user.deleted_at)}. Дані лишаються 30 днів. Hard-delete cron-ом (TODO post-launch).
                    </p>
                  </div>
                  <button
                    onClick={handleRestore}
                    disabled={actionLoading}
                    className="px-4 py-2 bg-[#7B9E7F] text-white rounded-lg text-sm font-medium hover:bg-[#6a8d6e] disabled:opacity-50 flex items-center gap-2"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Відновити користувача
                  </button>
                </div>
              ) : (
                <div className="bg-red-50 rounded-xl p-4 space-y-3">
                  <div>
                    <p className="text-sm font-medium text-red-600">Soft delete</p>
                    <p className="text-xs text-red-500 mt-0.5">
                      Юзер не зможе логінитись. Дані лишаються 30 днів для recovery. Після цього автоматичний hard-delete.
                    </p>
                  </div>
                  <button
                    onClick={handleSoftDelete}
                    disabled={actionLoading}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 disabled:opacity-50 flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Soft-delete юзера
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function fpAgo(at) {
  if (!at) return "";
  const d = (Date.now() - new Date(at).getTime()) / 86400000;
  if (d < 1) return "сьогодні";
  if (d < 2) return "учора";
  return Math.round(d) + "д";
}

const FP_DEMO = {
  meta: { total_events: 178, active_days: 63, days_since_last_activity: 0.1, confidence: 72 },
  scores: {
    rut:   { value: 78, available: true, tone: "warning", feeds: "cooked · унікальні рецепти · repeat rate", trend: "росте (було 64)", action: "Підкинути нову форму на вечерю у чт", rows: [["Унікальних страв","23"],["Повторюваність","2.0×"],["Топ-5 страв","80% готувань"],["Нових форм за 30д","1"]] },
    waste: { value: 22, available: true, tone: "success", feeds: "fridge_add / fridge_remove · причини", trend: "спадає", action: "Молоко 0.5 л · зелень меншими порціями", rows: [["Викинуто","9 з 84 продуктів"],["Найгірше","молоко 4/4"],["Втрачено","~$14"],["Shelf-life до викидання","8.5 дн"]] },
    goal:  { value: 64, available: true, tone: "warning", feeds: "cooked макроси vs ціль", trend: "стабільно", action: "Високобілкова вечеря у вт", rows: [["Сер. ккал/день","1920 проти 1800"],["Дні перевищення","7%"],["Білок hit-rate","71%"],["Найгірший день","вівторок"]] },
    nov:   { value: 35, available: true, tone: "accent", feeds: "swap · прийняття нових інгредієнтів", trend: "стабільно", action: "Пропонувати новинки дозовано", rows: [["Нових інгр. прийнято","35%"],["Swap-away з нових","6"],["Повторив новий рецепт","2 з 9"]] },
    churn: { value: 18, available: true, tone: "success", feeds: "активність · частота сесій", trend: "добре", action: "Моніторити, втручання не треба", rows: [["Сесій/тиждень","5.4"],["Тренд","зростає"],["Остання активність","2 год тому"]] },
  },
  recommendations: [
    ["waste", "Молоко псується 4/4 — пропонувати 0.5 л"],
    ["rut", "5 страв на 80% готувань — підкинути нову форму"],
    ["goal", "Білок недобір по вівторках — високобілкова вечеря у вт"],
    ["tonight", "Броколі ось-ось зіпсується — «приготуй сьогодні»"],
  ],
  loves: [["курка","~640 г/тиж"],["рис","~410 г/тиж"],["броколі","~300 г/тиж"],["яйце","~9 шт/тиж"]],
  avoids: [["кінза","заміна ×5"],["гриби","виключено"],["печінка","виключено"],["яловичина","заміна ×3"]],
  heat: {
    cook:   [[1,1,2,1,1,4,5],[5,3,3,5,4,7,7],[8,6,5,10,7,6,6]],
    kcal:   [[300,300,350,300,300,420,450],[600,520,500,620,540,700,680],[820,700,650,900,720,700,690]],
    time:   [[8,8,10,8,8,15,18],[20,18,15,22,16,28,26],[30,22,20,35,24,28,25]],
    fridge: [[40,40,60,40,40,70,75],[50,45,50,55,40,75,70],[85,60,55,100,70,65,60]],
  },
  predictions: [
    ["Закінчиться першим", "рис · ~3 дні", false],
    ["Зіпсується наступним", "броколі · ~1 день", true],
    ["Наступна покупка", "~субота", false],
    ["Траєкторія до цілі", "-0.4 кг / міс", false],
  ],
  groups: {
    cooked:   { label:"Приготував", tone:"success", n:47, cad:"кожні 1.9 дн", peak:"вечір · пн,чт", bars:[8,5,4,9,6,9,6], items:[["Курка з рисом і броколі","обід · будній","сьогодні"],["Омлет з овочами","сніданок · вихідний","учора"],["Суп харчо","обід · вихідний","2д"],["Запечена курка","вечеря · будній","3д"],["Салат з тунцем","вечеря · будній","4д"]] },
    like:     { label:"Лайк", tone:"success", n:31, cad:"кожні 2.9 дн", peak:"вечір", bars:[6,3,3,7,4,5,3], items:[["Курка з рисом і броколі","","сьогодні"],["Суп харчо","","2д"],["Запечена курка","","3д"]] },
    skip:     { label:"Пропустив", tone:"danger", n:14, cad:"кожні 6 дн", peak:"ранок", bars:[3,2,2,2,2,1,2], items:[["Паста болоньєзе","рейтинг","учора"],["Рибні котлети","план","3д"],["Овочеве рагу","рейтинг","6д"]] },
    surprise: { label:"Здивуй мене", tone:"accent", n:12, cad:"кожні 7 дн", peak:"вечеря", bars:[2,1,1,2,2,2,2], items:[["слот вечеря → прийнято","","учора"],["слот обід → перегенеровано","","7д"]] },
    purchase: { label:"Покупка", tone:"accent", n:11, cad:"кожні 6.2 дн", peak:"субота", bars:[1,0,1,1,2,5,1], items:[["14 позицій · $42","планова","3д"],["9 позицій · $28","імпульс","9д"]] },
    spoiled:  { label:"Зіпсувалось", tone:"danger", n:9, cad:"кожні 10 дн", peak:"молочка, зелень", bars:[2,1,1,1,1,2,1], items:[["Молоко 0.5 л","лежало 9 дн","сьогодні"],["Салат","лежав 5 дн","4д"],["Кінза","лежала 6 дн","7д"]] },
    regenerate: { label:"Перегенерував", tone:"danger", n:8, cad:"кожні 9 дн", peak:"вечеря", bars:[2,1,1,1,1,1,1], items:[["Овочеве рагу → Тако","відкинув рагу","2д"],["Паста → Салат","відкинув пасту","8д"]] },
    swap:     { label:"Заміна", tone:"danger", n:6, cad:"кожні 15 дн", peak:"ароматика, кислота", bars:[1,1,0,1,1,1,1], items:[["помідори → паприка","«Овочеве соте»","2д"],["кінза → петрушка","«Салат»","6д"]] },
    save:     { label:"Зберіг", tone:"success", n:7, cad:"кожні 13 дн", peak:"супи, випічка", bars:[1,1,1,1,1,1,1], items:[["Суп харчо","рецепт","4д"],["Як зберігати зелень","тіпс","7д"]] },
  },
};

const FP_TONE = {
  success: { text: "text-emerald-600", bar: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-600" },
  warning: { text: "text-amber-600", bar: "bg-amber-500", chip: "bg-amber-50 text-amber-600" },
  danger:  { text: "text-red-600", bar: "bg-red-500", chip: "bg-red-50 text-red-600" },
  accent:  { text: "text-sky-600", bar: "bg-sky-500", chip: "bg-sky-50 text-sky-600" },
};
const FP_SCORE_LABELS = { rut:"Кулінарна колія", waste:"Викиди", goal:"Складність цілі", nov:"Відкритість новому", churn:"Ризик відтоку" };
const FP_SCORE_ORDER = ["rut","waste","goal","nov","churn"];
const FP_DOW = ["пн","вт","ср","чт","пт","сб","нд"];
const FP_SLOTS = ["сніданок","обід","вечеря"];
const FP_HEAT_METRICS = [["cook","К-сть готувань"],["kcal","Сер. ккал"],["time","Сер. час, хв"],["fridge","% з холодильника"]];
const FP_GROUP_ORDER = ["cooked","like","skip","regenerate","surprise","purchase","spoiled","swap","save"];

function fpRealize(p) {
  const m = p.meta || {}, sc = p.scores || {}, h = p.history || {}, hm = p.heatmap || {}, la = p.loves_avoids || {};
  const scores = {};
  FP_SCORE_ORDER.forEach((id) => {
    const s = sc[id] || {};
    const tone = FP_DEMO.scores[id] ? FP_DEMO.scores[id].tone : "accent";
    scores[id] = {
      value: s.available ? s.value : null,
      available: !!s.available,
      tone,
      feeds: (s.basis || []).filter((x) => x !== "*all*").join(" · ") || "—",
      trend: "",
      action: "",
      reason: s.reason || "",
      rows: s.detail ? Object.entries(s.detail).map(([k, v]) => [k, String(v)]) : [],
    };
  });
  const rowSlots = ["breakfast","lunch","dinner"];
  const cook = rowSlots.map((sl) => [0,1,2,3,4,5,6].map((d) => (hm.counts && hm.counts[d] && hm.counts[d][sl]) || 0));
  const groups = {};
  FP_GROUP_ORDER.forEach((id) => {
    const g = h[id] || {};
    groups[id] = {
      label: FP_DEMO.groups[id].label,
      tone: FP_DEMO.groups[id].tone,
      n: g.count_total || 0,
      cad: g.cadence_days != null ? ("кожні " + g.cadence_days + " дн") : "—",
      peak: "—",
      available: g.available !== false,
      bars: (g.weekly || []).slice(-7),
      items: (g.recent || []).map((r) => [r.label || "—", "", fpAgo(r.at)]),
    };
  });
  return {
    meta: m,
    scores,
    recommendations: (p.recommendations || []).map((x) => [x.tag, x.text]),
    loves: (la.loves || []).map((x) => [x.ingredient, "×" + x.count]),
    avoids: (la.avoided_recipes || []).map((x) => [x.recipe, "×" + x.count]),
    heat: { cook, kcal: null, time: null, fridge: null },
    predictions: [],
    groups,
  };
}

function FpDemoBadge() {
  return <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100 text-gray-400 ml-2">demo</span>;
}

function FpHeat({ matrix, demo }) {
  if (!matrix) return <p className="text-xs text-gray-400">недоступно на реальних даних{demo ? "" : ""}</p>;
  let mx = 0;
  matrix.forEach((r) => r.forEach((v) => { if (v > mx) mx = v; }));
  return (
    <table className="w-full" style={{ borderSpacing: "4px", borderCollapse: "separate" }}>
      <tbody>
        <tr>
          <td style={{ width: "58px" }} />
          {FP_DOW.map((d) => <td key={d} className="text-center text-[10px] text-gray-400">{d}</td>)}
        </tr>
        {matrix.map((r, i) => (
          <tr key={i}>
            <td className="text-[11px] text-gray-500">{FP_SLOTS[i]}</td>
            {r.map((v, j) => (
              <td key={j}>
                <div className="h-5 rounded" style={{ background: "#7B9E7F", opacity: mx ? (0.1 + 0.9 * v / mx).toFixed(2) : 0.1 }} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function FpBars({ bars, tone }) {
  const mx = Math.max(...(bars.length ? bars : [1]), 1);
  return (
    <div className="flex items-end gap-1.5 h-16">
      {bars.map((v, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <div className={"w-full rounded " + (FP_TONE[tone] ? FP_TONE[tone].bar : "bg-sky-500")} style={{ height: Math.round(v / mx * 46) + 3 + "px" }} />
          <span className="text-[9px] text-gray-400">{FP_DOW[i]}</span>
        </div>
      ))}
    </div>
  );
}

function FoodProfileTab({ api, userId }) {
  const [profile, setProfile] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [mode, setMode] = React.useState("demo");
  const [view, setView] = React.useState("overview");
  const [scoreSel, setScoreSel] = React.useState(null);
  const [heatMetric, setHeatMetric] = React.useState("cook");
  const [grpSel, setGrpSel] = React.useState("cooked");

  React.useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError(false);
      try {
        const res = await api().get(`/admin/users/${userId}/profile`);
        if (alive) setProfile(res.data);
      } catch (e) {
        console.error(e);
        if (alive) setError(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [api, userId]);

  if (loading) return <div className="flex justify-center pt-10"><div className="w-8 h-8 border-2 border-[#7B9E7F]/20 border-t-[#7B9E7F] rounded-full animate-spin" /></div>;
  if (error || !profile) return <p className="text-red-500 text-sm">Не вдалося завантажити профіль</p>;

  const real = fpRealize(profile);
  const isDemo = mode === "demo";
  const D = isDemo ? FP_DEMO : real;
  const m = D.meta || {};
  const dBadge = !isDemo;

  const VIEWS = [["overview","Огляд"],["habits","Звички"],["history","Історія дій"],["predict","Прогнози"]];

  const scoreDetail = scoreSel ? D.scores[scoreSel] : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-baseline gap-3">
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Харчовий профіль</h3>
          <span className="text-xs text-gray-400">
            {(m.total_events || 0)} подій · днів: {m.active_days || 0}
            {m.days_since_last_activity != null ? ` · активн. ${m.days_since_last_activity} дн тому` : ""}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-[9px] text-gray-400 uppercase leading-none">Впевненість</p>
            <p className="text-lg font-semibold text-[#7B9E7F] leading-tight">{m.confidence != null ? m.confidence : 0}%</p>
          </div>
          <div className="flex rounded-lg bg-gray-100 p-0.5 text-xs">
            <button onClick={() => setMode("real")} className={"px-2.5 py-1 rounded-md " + (!isDemo ? "bg-white shadow-sm text-[#3D4035] font-medium" : "text-gray-400")}>Real</button>
            <button onClick={() => setMode("demo")} className={"px-2.5 py-1 rounded-md " + (isDemo ? "bg-white shadow-sm text-[#3D4035] font-medium" : "text-gray-400")}>Demo</button>
          </div>
        </div>
      </div>

      {!isDemo && (m.total_events || 0) === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-xs text-amber-700">
          Реальних подій ще нема — панелі порожні. Перемкни на <b>Demo</b>, щоб побачити наповнений вигляд.
        </div>
      )}

      <div className="flex gap-1 border-b border-black/5">
        {VIEWS.map(([id, label]) => (
          <button key={id} onClick={() => { setView(id); setScoreSel(null); }}
            className={"px-3 py-2 text-sm font-medium border-b-2 -mb-px " + (view === id ? "border-[#7B9E7F] text-[#3D4035]" : "border-transparent text-gray-400 hover:text-gray-600")}>
            {label}
          </button>
        ))}
      </div>

      {view === "overview" && !scoreSel && (
        <div className="space-y-5">
          <div>
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2">Скори <span className="text-gray-300 normal-case">· клік → розбір</span></p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {FP_SCORE_ORDER.map((id) => {
                const s = D.scores[id] || {};
                const tone = FP_TONE[s.tone] || FP_TONE.accent;
                return (
                  <button key={id} onClick={() => s.available && setScoreSel(id)} className="text-left bg-white rounded-xl border border-black/5 p-3 hover:border-[#7B9E7F]/40 transition-colors">
                    <p className="text-[11px] text-gray-500 mb-1 leading-tight flex items-center justify-between">{FP_SCORE_LABELS[id]} <ChevronRight className="w-3 h-3 text-gray-300" /></p>
                    {s.available ? (
                      <>
                        <p className={"text-xl font-semibold " + tone.text}>{s.value}</p>
                        <div className="h-1.5 bg-gray-100 rounded-full mt-1.5 overflow-hidden"><div className={"h-full rounded-full " + tone.bar} style={{ width: s.value + "%" }} /></div>
                      </>
                    ) : (
                      <><p className="text-xl font-semibold text-gray-300">—</p><p className="text-[10px] text-gray-400 mt-1 leading-tight">недостатньо даних</p></>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2">Рекомендовані дії</p>
            {((D.recommendations && D.recommendations.length) ? D.recommendations : null) ? (
              <div className="bg-white rounded-xl border border-black/5 divide-y divide-black/5">
                {D.recommendations.map(([tag, text], i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-2.5">
                    <span className="text-sm flex-1 text-[#3D4035]">{text}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{tag}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-black/5 px-3 py-2.5 text-sm text-gray-400">Поки нема рекомендацій — замало даних.</div>
            )}
          </div>

          <div>
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2">Любить / уникає</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-white rounded-xl border border-black/5 p-3">
                <p className="text-xs font-medium text-emerald-600 mb-2">Любить</p>
                {(D.loves && D.loves.length ? D.loves : [["—",""]]).map(([k, v], i) => (
                  <div key={i} className="flex justify-between text-sm py-0.5"><span>{k}</span><span className="text-gray-400">{v}</span></div>
                ))}
              </div>
              <div className="bg-white rounded-xl border border-black/5 p-3">
                <p className="text-xs font-medium text-red-500 mb-2">Уникає</p>
                {(D.avoids && D.avoids.length ? D.avoids : [["—",""]]).map(([k, v], i) => (
                  <div key={i} className="flex justify-between text-sm py-0.5"><span>{k}</span><span className="text-gray-400">{v}</span></div>
                ))}
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Коли готує</p>
              <select value={heatMetric} onChange={(e) => setHeatMetric(e.target.value)} className="text-xs border border-black/10 rounded-lg px-2 py-1">
                {FP_HEAT_METRICS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="bg-white rounded-xl border border-black/5 p-3">
              <FpHeat matrix={D.heat ? D.heat[heatMetric] : null} demo={dBadge} />
              {!isDemo && heatMetric !== "cook" && <p className="text-[11px] text-gray-400 mt-2">Метрика доступна лише на demo — потребує макросів / часу готування.</p>}
            </div>
          </div>

          <div>
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2">Прогнози {dBadge && <FpDemoBadge />}</p>
            <div className="bg-white rounded-xl border border-black/5 divide-y divide-black/5">
              {FP_DEMO.predictions.slice(0, 2).map(([k, v, warn], i) => (
                <div key={i} className="flex justify-between text-sm px-3 py-2"><span className="text-gray-500">{k}</span><span className={"font-medium " + (warn ? "text-amber-600" : "text-[#3D4035]")}>{v}</span></div>
              ))}
            </div>
          </div>
        </div>
      )}

      {view === "overview" && scoreSel && scoreDetail && (
        <div className="space-y-3">
          <button onClick={() => setScoreSel(null)} className="text-xs text-gray-400 flex items-center gap-1 hover:text-gray-600"><ChevronLeft className="w-3 h-3" /> назад до скорів</button>
          <div className="flex justify-between items-baseline">
            <h2 className="text-lg font-medium text-[#3D4035]">{FP_SCORE_LABELS[scoreSel]}</h2>
            <span className={"text-2xl font-semibold " + (FP_TONE[scoreDetail.tone] || FP_TONE.accent).text}>{scoreDetail.value != null ? scoreDetail.value : "—"}</span>
          </div>
          <div className="bg-white rounded-xl border border-black/5 p-3">
            {(scoreDetail.rows && scoreDetail.rows.length ? scoreDetail.rows : [["Даних поки нема", scoreDetail.reason || ""]]).map(([k, v], i) => (
              <div key={i} className="flex justify-between text-sm py-1"><span className="text-gray-500">{k}</span><span className="font-medium">{v}</span></div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-xl border border-black/5 p-3"><p className="text-[11px] text-gray-400 mb-1">Живиться з</p><p className="text-xs">{scoreDetail.feeds || "—"}</p></div>
            <div className="bg-white rounded-xl border border-black/5 p-3"><p className="text-[11px] text-gray-400 mb-1">Тренд</p><p className="text-xs">{scoreDetail.trend || "—"}</p></div>
          </div>
          {scoreDetail.action && <div className="bg-[#7B9E7F]/10 border border-[#7B9E7F]/30 rounded-xl p-3"><p className="text-[11px] text-[#7B9E7F] mb-1">Рекомендована дія</p><p className="text-sm text-[#3D4035]">{scoreDetail.action}</p></div>}
        </div>
      )}

      {view === "habits" && (
        <div className="space-y-3">
          <div className="bg-white rounded-xl border border-black/5 p-3">
            <p className="text-xs font-medium text-[#3D4035] mb-2">Інгредієнти</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[11px] text-emerald-600 mb-1">Любить</p>
                {(D.loves && D.loves.length ? D.loves : [["—",""]]).map(([k, v], i) => <div key={i} className="flex justify-between text-xs py-0.5"><span>{k}</span><span className="text-gray-400">{v}</span></div>)}
              </div>
              <div>
                <p className="text-[11px] text-red-500 mb-1">Уникає</p>
                {(D.avoids && D.avoids.length ? D.avoids : [["—",""]]).map(([k, v], i) => <div key={i} className="flex justify-between text-xs py-0.5"><span>{k}</span><span className="text-gray-400">{v}</span></div>)}
              </div>
            </div>
          </div>
          {[["Готування","репертуар, покриття слотів, час, конверсія plan→cooked"],["Фрідж","waste rate, утилізація, хронічні викиди, швидкість споживання"],["Покупки","каденс, кошик, план vs імпульс, стейпли"],["Цілі","ккал vs ціль, білок hit-rate, adherence streak"],["Інтерес","зберігає рецепти/тіпси, теми, каденс «Здивуй мене»"],["Мікроелементи","залізо, віт.D, кальцій (beta)"]].map(([title, sub]) => (
            <div key={title} className="bg-white rounded-xl border border-black/5 p-3 flex items-center justify-between">
              <div><p className="text-xs font-medium text-[#3D4035]">{title}</p><p className="text-[11px] text-gray-400">{sub}</p></div>
              <FpDemoBadge />
            </div>
          ))}
        </div>
      )}

      {view === "history" && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {FP_GROUP_ORDER.map((k) => {
              const g = D.groups[k] || {};
              const on = grpSel === k;
              return (
                <button key={k} onClick={() => setGrpSel(k)} disabled={g.available === false}
                  className={"px-2.5 py-1 rounded-full text-xs " + (on ? "bg-[#7B9E7F] text-white" : g.available === false ? "bg-gray-50 text-gray-300" : "bg-gray-100 text-gray-500 hover:bg-gray-200")}>
                  {(FP_DEMO.groups[k] || {}).label} {g.n || 0}
                </button>
              );
            })}
          </div>
          {(() => {
            const g = D.groups[grpSel] || {};
            if (g.available === false) return <p className="text-xs text-gray-400 bg-white rounded-xl border border-black/5 p-3">Джерело «swap» ще не існує (фіча заміни інгредієнта не збудована).</p>;
            return (
              <div className="bg-white rounded-xl border border-black/5 p-3 space-y-3">
                <div className="flex justify-between items-baseline">
                  <span className="text-sm font-medium text-[#3D4035]">{(FP_DEMO.groups[grpSel] || {}).label}</span>
                  <span className="text-xs text-gray-400">{g.n || 0} за 90д · {g.cad || "—"}{g.peak && g.peak !== "—" ? " · пік: " + g.peak : ""}</span>
                </div>
                {g.bars && g.bars.length ? <FpBars bars={g.bars} tone={g.tone} /> : null}
                <div className="divide-y divide-black/5">
                  {(g.items && g.items.length ? g.items : [["записів нема","",""]]).map(([t, meta, when], i) => (
                    <div key={i} className="flex items-center gap-2 py-1.5 text-sm">
                      <span className="flex-1 min-w-0 truncate"><span className="font-medium">{t}</span>{meta ? <span className="text-gray-400"> · {meta}</span> : null}</span>
                      <span className="text-xs text-gray-400 flex-shrink-0">{when}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {view === "predict" && (
        <div className="space-y-2">
          <p className="text-[11px] text-gray-400 uppercase tracking-wider">Прогнози <FpDemoBadge /></p>
          <div className="bg-white rounded-xl border border-black/5 divide-y divide-black/5">
            {FP_DEMO.predictions.map(([k, v, warn], i) => (
              <div key={i} className="flex justify-between text-sm px-3 py-2.5"><span className="text-gray-500">{k}</span><span className={"font-medium " + (warn ? "text-amber-600" : "text-[#3D4035]")}>{v}</span></div>
            ))}
          </div>
          <p className="text-[11px] text-gray-400">Прогнози рахуються з каденсу подій + швидкості споживання; будуть реальними, коли накопичиться історія.</p>
        </div>
      )}
    </div>
  );
}

function ProductsSection({ api }) {
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState({});
  const [selectedCat, setSelectedCat] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api().get("/admin/unknown-products");
      setProducts(res.data.products || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (name) => {
    const cat = selectedCat[name] || "other";
    setResolving((p) => ({ ...p, [name]: true }));
    try {
      await api().post(`/admin/unknown-products/${encodeURIComponent(name)}/resolve`, { category: cat });
      setProducts((p) => p.filter((x) => x.name !== name));
    } finally {
      setResolving((p) => ({ ...p, [name]: false }));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-[#3D4035]">Невідомі продукти <span className="text-sm font-normal text-gray-400">({products.length})</span></h2>
        <button onClick={load} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
          <RefreshCw className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center pt-10"><div className="w-8 h-8 border-2 border-[#7B9E7F]/20 border-t-[#7B9E7F] rounded-full animate-spin" /></div>
      ) : products.length === 0 ? (
        <div className="text-center text-gray-400 pt-10">
          <Package className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p>{t("adminAllRecognized")}</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-black/5 divide-y divide-gray-50">
          {products.map((p) => (
            <div key={p.name} className="flex items-center gap-3 px-4 py-3">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#3D4035] truncate">{p.name}</p>
                <p className="text-xs text-gray-400">Зустрічається: {p.count || 1} разів</p>
              </div>
              <select
                value={selectedCat[p.name] || "other"}
                onChange={(e) => setSelectedCat((prev) => ({ ...prev, [p.name]: e.target.value }))}
                className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-[#3D4035] focus:outline-none focus:border-[#7B9E7F]"
              >
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <button
                onClick={() => resolve(p.name)}
                disabled={resolving[p.name]}
                className="px-3 py-1.5 bg-[#7B9E7F] text-white rounded-lg text-sm font-medium hover:bg-[#6a8d6e] transition-colors disabled:opacity-50 flex items-center gap-1"
              >
                {resolving[p.name] ? <div className="w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                OK
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SupportSection({ api }) {
  const { t } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("unread");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api().get(`/admin/support?read_filter=${filter}`);
      setMessages(res.data.messages || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [api, filter]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (msg) => {
    if (!msg.read) {
      await api().put(`/admin/support/${msg.id}/read`);
      setMessages((prev) => prev.map((m) => m.id === msg.id ? { ...m, read: true } : m));
    }
    setSelected(msg);
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleString("uk-UA", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-[#3D4035]">Підтримка</h2>
        <button onClick={load} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
          <RefreshCw className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      <div className="flex gap-2 mb-4">
        {["unread", "all", "read"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filter === f ? "bg-[#7B9E7F] text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
          >
            {f === "unread" ? t("adminNew") : f === "all" ? t("adminAll") : t("adminRead")}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center pt-10"><div className="w-8 h-8 border-2 border-[#7B9E7F]/20 border-t-[#7B9E7F] rounded-full animate-spin" /></div>
      ) : messages.length === 0 ? (
        <div className="text-center text-gray-400 pt-10">
          <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p>{t("adminNoMessages")}</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-black/5 divide-y divide-gray-50">
          {messages.map((m) => (
            <button
              key={m.id}
              onClick={() => markRead(m)}
              className="w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50/50 text-left transition-colors"
            >
              <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${m.read ? "bg-gray-200" : "bg-[#7B9E7F]"}`} />
              <div className="flex-1 min-w-0">
                <p className={`text-sm truncate ${m.read ? "text-gray-500" : "font-medium text-[#3D4035]"}`}>
                  {m.subject || m.category || "Повідомлення"}
                </p>
                <p className="text-xs text-gray-400 truncate">{m.user_email} · {fmtDate(m.created_at)}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0 mt-0.5" />
            </button>
          ))}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-end md:items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="font-semibold text-[#3D4035]">{selected.subject || "Без теми"}</p>
                <p className="text-xs text-gray-400 mt-0.5">{selected.user_name} · {selected.user_email}</p>
              </div>
              <button onClick={() => setSelected(null)} className="p-1 rounded-lg hover:bg-gray-100">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-700 whitespace-pre-wrap min-h-[80px]">
              {selected.message}
            </div>
            <p className="text-xs text-gray-400 mt-3">
              Категорія: {selected.category} · {new Date(selected.created_at).toLocaleString("uk-UA")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

const SIDEBAR_COLLAPSED_KEY = "admin_sidebar_collapsed";

export default function AdminPage() {
  const { user, logout, api } = useAuth();
  const { t } = useLanguage();
  const [section, setSection] = useState("dashboard");
  const [verified, setVerified] = useState(() => sessionStorage.getItem("admin_verified") === "1");
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1");

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? "1" : "0");
      return next;
    });
  };

  if (!verified) {
    return <AdminLock api={api} onUnlock={() => setVerified(true)} />;
  }

  const SECTION_MAP = {
    dashboard: <Dashboard api={api} />,
    users: <UsersSection api={api} />,
    products: <UnknownProductsQueue api={api} />,
    support: <SupportSection api={api} />,
  };

  const asideWidth = collapsed ? "w-16" : "w-56";

  return (
    <div className="fixed inset-0 bg-[#F5F5F3] flex overflow-y-auto">
      {/* Sidebar */}
      <aside className={`${asideWidth} bg-white border-r border-black/5 flex flex-col py-6 px-2 fixed h-full z-20 transition-all duration-200 ${!collapsed ? "shadow-2xl" : ""}`}>
        {/* Header with email + toggle */}
        <div className={`flex items-center mb-6 ${collapsed ? "justify-center" : "justify-between px-2"}`}>
          {!collapsed && (
            <div className="min-w-0">
              <p className="text-xs text-gray-400 uppercase tracking-widest mb-0.5">Admin</p>
              <p className="font-semibold text-[#3D4035] text-sm truncate">{user?.email}</p>
            </div>
          )}
          <button
            onClick={toggleCollapsed}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-[#3D4035] transition-colors flex-shrink-0"
            title={collapsed ? "Розгорнути меню" : "Згорнути меню"}
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 space-y-1">
          {SECTION_IDS.map(({ id, tKey, icon: Icon }) => {
            const active = section === id;
            return (
              <button
                key={id}
                onClick={() => setSection(id)}
                title={collapsed ? t(tKey) : ""}
                className={`w-full flex items-center gap-3 rounded-xl text-sm font-medium transition-colors ${
                  collapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"
                } ${
                  active
                    ? "bg-[#7B9E7F]/10 text-[#7B9E7F]"
                    : "text-gray-500 hover:bg-gray-50"
                }`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                {!collapsed && <span className="truncate">{t(tKey)}</span>}
              </button>
            );
          })}
        </nav>

        {/* Logout */}
        <button
          onClick={() => { sessionStorage.removeItem("admin_verified"); logout(); }}
          title={collapsed ? t("adminLogout") : ""}
          className={`flex items-center gap-2 rounded-xl text-sm text-red-400 hover:bg-red-50 transition-colors ${
            collapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"
          }`}
        >
          <LogOut className="w-4 h-4 flex-shrink-0" />
          {!collapsed && <span>{t("adminLogout")}</span>}
        </button>
      </aside>

      {/* Main content */}
      <main className="ml-16 flex-1 p-6 lg:p-8">
        {SECTION_MAP[section]}
      </main>
    </div>
  );
}
